import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, A11y } from 'swiper/modules';
import LoadingState from '../../ui/LoadingState';
import ProgressiveImage from '../../ui/ProgressiveImage';
import type { MediaItem } from '../../../types/animatronic';
import 'swiper/css';
import 'swiper/css/navigation';

interface AnimatronicCardBaseProps {
  name: string;
  type: string;
  media: MediaItem[];
  isLoading: boolean;
  onClick: (e: React.MouseEvent) => void;
  allowNavigation?: boolean;
  disableCardClick?: boolean;
  compact?: boolean;
  children: React.ReactNode;
}

const AnimatronicCardBase: React.FC<AnimatronicCardBaseProps> = ({
  name,
  type,
  media,
  isLoading,
  onClick,
  allowNavigation = false,
  disableCardClick = false,
  compact = false,
  children
}) => {
  const imageMedia = media.filter(item => item.type === 'image');
  const hasMultipleImages = imageMedia.length > 1;
  const randomDelay = React.useMemo(() => 
    Math.floor(Math.random() * (7000 - 3000) + 3000)
  , []);

  if (isLoading) {
    return <LoadingState type="skeleton" />;
  }

  return (
    <div
      className={`bg-[#2A2A2A] rounded-xl overflow-hidden border border-gray-800 hover:border-brand/50 transition-all group hover:shadow-[0_0_15px_rgba(62,207,142,0.3)] duration-300 h-full ${
        !disableCardClick ? 'cursor-pointer' : ''
      }`}
      onClick={disableCardClick ? undefined : onClick}
    >
      <div className="relative">
        <Swiper
          modules={[Navigation, Autoplay, A11y]}
          navigation={allowNavigation && hasMultipleImages}
          loop={hasMultipleImages}
          autoplay={hasMultipleImages ? {
            delay: randomDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          } : false}
          className={`${compact ? 'aspect-[4/3]' : 'aspect-video'}`}
        >
          {imageMedia.map((item, index) => (
            <SwiperSlide key={`${item.url}-${index}`}>
              <div className="relative w-full h-full">
                <ProgressiveImage
                  src={item.url}
                  alt={`${name} - Image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand/20 text-brand backdrop-blur-sm">
            {type.split('-').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')}
          </span>
        </div>
      </div>
      
      <div className={`${compact ? 'p-3' : 'p-5'}`}>
        {children}
      </div>
    </div>
  );
};

export default AnimatronicCardBase;